import { useMemo, useState } from 'react';
import { GrowthDashboardTopBarActions, PatientLeadsView, type LdStatusKey } from '@elva/ui';
import { AsyncBoundary } from '@/components/feedback';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { viewProps } from '@/data/viewProps';
import { useLeadDetail, useLeadMutations, useLeads } from '../hooks/useLeads';

export function LeadsPage() {
  usePageTopBar({
    actions: <GrowthDashboardTopBarActions />,
  });

  const { data, isPending, isError, error, refetch, isRefetching, isFetching } = useLeads();
  const { updateStatus } = useLeadMutations();
  const [expandedLeadId, setExpandedLeadId] = useState<number | null>(null);
  const expandedBackendId =
    expandedLeadId != null ? data?.idMap[expandedLeadId] : undefined;
  const {
    data: expandedLead,
    isFetching: expandedLeadLoading,
  } = useLeadDetail(expandedBackendId, expandedLeadId);

  const leadDetails = useMemo(
    () => (expandedLeadId != null && expandedLead ? { [expandedLeadId]: expandedLead } : undefined),
    [expandedLead, expandedLeadId],
  );

  return (
    <AsyncBoundary
      loading={isPending}
      error={data === undefined && isError ? error : undefined}
      onRetry={refetch}
      retrying={isRefetching}
      isEmpty={data !== undefined && data.items.length === 0}
    >
      <PatientLeadsView
        {...viewProps({
          items: data?.items,
          metrics: data?.metrics,
          campaignOptions: data?.campaignOptions,
          loading: isFetching,
          onLeadExpand: setExpandedLeadId,
          leadDetails,
          leadDetailLoadingId: expandedLeadLoading ? expandedLeadId : null,
          onStatusChange: (leadId: number, status: LdStatusKey) => {
            const backendId = data?.idMap[leadId];
            if (backendId) updateStatus.mutate({ id: backendId, status });
          },
        })}
      />
    </AsyncBoundary>
  );
}
